/**
 * Team-panel "Manage Work" / "Manage HR" section headers are driven by
 * everyone-mandate and everyone-screenshots. When either flag flips, live sync
 * must refetch the viewer's own root team-panel payload (the only response that
 * includes the HR batch) so HR members appear or drop without a full page refresh.
 */

export const EVERYONE_TEAM_PANEL_PERMISSIONS = [
    "can-set-everyone-mandate",
    "can-view-everyone-screenshots",
];

export const SCREENSHOT_ACCESS_PERMISSIONS = [
    "can-view-everyone-screenshots",
    "can-view-downline-screenshots",
];

/**
 * @param {Record<string, unknown>|null|undefined} previousPermissions
 * @param {Record<string, unknown>|null|undefined} nextPermissions
 * @returns {boolean}
 */
export function hasEveryoneTeamPanelScopeChanged(
    previousPermissions,
    nextPermissions,
) {
    return EVERYONE_TEAM_PANEL_PERMISSIONS.some(
        (key) =>
            Boolean(previousPermissions?.[key]) !==
            Boolean(nextPermissions?.[key]),
    );
}

/**
 * Team-panel Screenshots tab access is driven by everyone- and downline-
 * screenshot permissions. When either flag flips, live sync must drop or
 * refetch the already-open screenshot list — switching members is not required.
 *
 * @param {Record<string, unknown>|null|undefined} previousPermissions
 * @param {Record<string, unknown>|null|undefined} nextPermissions
 * @returns {boolean}
 */
export function hasScreenshotAccessPermissionChanged(
    previousPermissions,
    nextPermissions,
) {
    return SCREENSHOT_ACCESS_PERMISSIONS.some(
        (key) =>
            Boolean(previousPermissions?.[key]) !==
            Boolean(nextPermissions?.[key]),
    );
}

/**
 * Profile IDs present on a team-panel payload (viewer root + work downline + HR batch).
 *
 * @param {Record<string, unknown>|null|undefined} payload
 * @returns {Set<number>}
 */
export function collectAccessibleTeamPanelProfileIds(payload) {
    const ids = new Set();

    const walk = (node) => {
        if (!node || typeof node !== "object") {
            return;
        }

        const id = Number(node.user_profile_id ?? node.id);
        if (id && !Number.isNaN(id)) {
            ids.add(id);
        }

        if (Array.isArray(node.subordinates)) {
            node.subordinates.forEach(walk);
        }
    };

    walk(payload?.data ?? payload);

    return ids;
}

/**
 * True when the current selection or drill-down target is no longer on the
 * viewer's refreshed root panel (typical after everyone-scope revoke).
 *
 * @param {{
 *   viewerId: number|string|null|undefined,
 *   selectedId: number|string|null|undefined,
 *   drilledId: number|string|null|undefined,
 *   accessibleIds: Set<number>,
 * }} args
 * @returns {boolean}
 */
export function shouldResetEveryoneTeamPanelSelection({
    viewerId,
    selectedId,
    drilledId,
    accessibleIds,
}) {
    const viewer = Number(viewerId);

    const isInaccessible = (id) => {
        if (id == null || id === "") {
            return false;
        }

        const n = Number(id);
        if (!n || Number.isNaN(n) || n === viewer) {
            return false;
        }

        return !accessibleIds.has(n);
    };

    return isInaccessible(selectedId) || isInaccessible(drilledId);
}

/**
 * Profile the open Screenshots tab is showing (drilled subordinate, else member).
 *
 * @param {{
 *   selectedSubordinateId: number|string|null|undefined,
 *   selectedMemberId: number|string|null|undefined,
 * }} args
 * @returns {number|null}
 */
export function currentTeamPanelScreenshotProfileId({
    selectedSubordinateId,
    selectedMemberId,
}) {
    const subordinate = Number(selectedSubordinateId);
    if (
        selectedSubordinateId != null &&
        selectedSubordinateId !== "" &&
        subordinate &&
        !Number.isNaN(subordinate)
    ) {
        return subordinate;
    }

    const member = Number(selectedMemberId);
    if (
        selectedMemberId != null &&
        selectedMemberId !== "" &&
        member &&
        !Number.isNaN(member)
    ) {
        return member;
    }

    return null;
}

/**
 * True when a screenshot response is for a member who is no longer selected
 * (typical after everyone-screenshots revoke drops an HR row and re-selects
 * the viewer). fetchId alone cannot catch this when the stale request is
 * still the latest generation.
 *
 * @param {{
 *   requestedProfileId: number|string|null|undefined,
 *   selectedSubordinateId: number|string|null|undefined,
 *   selectedMemberId: number|string|null|undefined,
 * }} args
 * @returns {boolean}
 */
export function isStaleScreenshotProfileResponse({
    requestedProfileId,
    selectedSubordinateId,
    selectedMemberId,
}) {
    if (requestedProfileId == null || requestedProfileId === "") {
        return true;
    }

    const current = currentTeamPanelScreenshotProfileId({
        selectedSubordinateId,
        selectedMemberId,
    });

    if (current == null) {
        return true;
    }

    return Number(requestedProfileId) !== current;
}
